import { useCallback, useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import type { LvGesamt, LvGrundlagen } from '../api';
import type { Rahmen } from './Projektrahmen';
import { PageHeader } from '../rahmen/PageHeader';
import { ErrorState } from '../muster';
import { GEWERKE, GesamtLv, Grundlagen } from './Ausschreibung';

/**
 * Ausschreibung > Gesamt-LV / Grundlagen — dünne Routenhüllen (N3-Nachtrag).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DIESE ZWEI UND NICHT DIE DRITTE ("MENGEN" ALS EIGENSTÄNDIGE SEITE)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Plan Abschnitt 3.8 nennt drei Berechnungen: Mengen (`lv_mengenregeln`,
 * `lv_positionselemente`), Kennwerte/Kosten (`lv_kennwertregeln`,
 * `lv_kostengruppen`), Gesamt-LV (`lv_gesamt`, `lv_gesamt_laeufe`).
 * Gemessen (Migrationen, nicht nur Tabellenname):
 *
 *   - `lv_kennwertregeln` entstand in 0541_flb_kennwertregeln.sql,
 *     `lv_kostengruppen` in 0513_lv_kostengruppen_und_funktionstexte.sql -
 *     beide gehören zur FUNKTIONALEN Ausschreibung (`LvKennwertregel` in
 *     `Funktionalbeschreibung.cs`, nicht in `Ausschreibung.cs`). Sie sind
 *     bereits `Funktionalbeschreibung.tsx` = Route `funktionalbeschreibung`,
 *     seit diesem Paket ein Kind von `ausschreibung`. "Kennwerte/Kosten"
 *     hat also schon eine Route - keine dritte Seite nötig.
 *   - `lv_mengenregeln` UND `lv_merkmalregeln` (nicht `lv_kennwertregeln`)
 *     stehen zusammen mit Textbausteinen/Vorbemerkungen/Einheiten in EINER
 *     Abfrage (`api.lvGrundlagen`) und werden vom bisherigen Reiter
 *     "Grundlagen" gezeigt (Kennzahlen "Mengenregeln"/"Merkmalregeln", der
 *     Probelauf rechnet genau `lv_mengenregeln` neu). Das IST die
 *     "Mengen"-Berechnung - unten als `AusschreibungGrundlagenSeite`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM `AusschreibungGrundlagenSeite` KEINEN SIDEBAR-EINTRAG BEKOMMT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Anders als `AusschreibungGesamtSeite` (projektweit, `lvGesamt` kennt kein
 * Gewerk) haengt `Grundlagen` am `:gewerk`-Parameter der bestehenden Route
 * `ausschreibung/:gewerk` - genau dieselbe Bindung, aus der bereits der
 * Kopfkommentar von `navigation/struktur.ts` (Zeile 77-80) UND der
 * Vollstaendigkeitstest (`struktur.test.ts`, Filter `!p.includes(':')`)
 * folgern, dass `ausschreibung` selbst auf die Weiterleitungsroute zeigt
 * und NICHT auf ein fest gewaehltes Gewerk: ein Sidebar-Eintrag braucht
 * GENAU EINEN Pfad, und "welches der sieben Gewerke" hat hier dieselbe
 * fehlende Antwort wie beim Gewerksknoten `ausschreibung` selbst. Die Route
 * `ausschreibung/:gewerk/grundlagen` existiert trotzdem (siehe App.tsx) und
 * ist ueber die Gewerke-Reiter der Ausschreibungsseite erreichbar - nur
 * eben nicht ueber die Seitenleiste, aus demselben Grund wie ihr Elternpfad.
 */

export function AusschreibungGesamtSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const [daten, setDaten] = useState<LvGesamt | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    setDaten(null);
    setFehler(null);
    api.lvGesamt(projekt.id)
      .then(setDaten)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt.id]);

  return (
    <>
      <PageHeader
        titel="Ausschreibung · Gesamt-LV"
        beschreibung={'Fasst die je Gewerk abgeleiteten Lose zusammen - projektweit, '
          + 'nicht an ein Gewerk gebunden. Zwei Gegenproben ueber die Losgrenze hinweg: '
          + 'Doppelzuordnung und Summenkontrolle.'}
      />
      <GesamtLv
        daten={daten} fehler={fehler} api={api}
        projektId={projekt.id} projektnummer={projekt.number}
      />
    </>
  );
}

export function AusschreibungGrundlagenSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const { gewerk = '' } = useParams<{ gewerk: string }>();
  const fach = GEWERKE[gewerk];
  const [daten, setDaten] = useState<LvGrundlagen | null>(null);

  const laden = useCallback(() => {
    setDaten(null);
    api.lvGrundlagen(projekt.id, gewerk).then(setDaten);
  }, [api, projekt.id, gewerk]);

  useEffect(() => { if (fach) laden(); }, [fach, laden]);

  if (!fach) {
    return (
      <ErrorState
        titel={`Unbekanntes Gewerk: ${gewerk}`}
        meldung={'Die Adresse nennt ein Gewerk, das es in der Ausschreibung nicht gibt. '
          + `Bekannt sind: ${Object.values(GEWERKE).map((g) => g.name).join(', ')}.`}
      />
    );
  }

  return (
    <>
      <PageHeader
        titel={`Ausschreibung · ${fach.name} · Grundlagen`}
        beschreibung={'Die Mengen- und Merkmalregeln, aus denen die Positionen '
          + 'entstehen, mit Textbausteinen, Vorbemerkungen, Einheiten und dem '
          + 'Probelauf, der den Zahlenvergleich vor jeder Aenderung zeigt.'}
      />
      <Grundlagen
        daten={daten} api={api} projektId={projekt.id}
        gewerk={gewerk} neuLaden={laden}
      />
    </>
  );
}
